/**
 * /business/demo — pitch sandbox.
 *
 * Faithfully replicates the real user's prediction card (see MatchCard in
 * pages/MatchesPage.jsx) so a pub owner sees the exact same three-row grid
 * (Prediction / Actual / Points) they'll see on real match-days.
 *
 * All reads/writes go through /api/business-demo/* which uses the isolated
 * `demo_predictions` MongoDB collection. Nothing here can touch real match
 * standings or trigger real emails.
 *
 * Admins additionally see a fixed top toolbar with two buttons:
 * Simulate Final Whistle and Reset Demo.
 */
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PREDICTION_FIELDS } from "@/lib/constants";
import { clearDemoLock, isDemoLocked } from "@/components/DemoLockGuard";
import EmailPreviewDialog from "@/components/EmailPreviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Beer,
  Trophy,
  Loader2,
  RotateCcw,
  AlarmClock,
  CheckCircle2,
  Sparkles,
  Lock,
  LogOut,
  Mail,
  Bell,
  ClipboardList,
  Users,
  MessageCircle,
  Award,
  Ticket,
} from "lucide-react";

const SESSION_KEY = "demo_session_id";
const NAME_KEY = "demo_display_name";

function ensureSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `viewer-${crypto.randomUUID().slice(0, 12)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

// ---------------------------------------------------------------------------
// Points helpers — kept locally to mirror MatchesPage without a circular dep.
// ---------------------------------------------------------------------------
function pointsFor(pred, actual, fieldKey) {
  if (pred == null || pred === "" || actual == null) return 0;
  const diff = Math.abs(parseInt(pred, 10) - parseInt(actual, 10));
  if (fieldKey === "throw_ins") return diff <= 5 ? 2 : 0;
  if (diff === 0) return 3;
  return diff === 1 ? 1 : 0;
}

function getPointsPillClass(value) {
  if (value === 3) return "bg-[#0038FF] text-white";
  if (value === 2) return "bg-emerald-600 text-white";
  if (value === 1) return "bg-zinc-950 text-white";
  return "bg-zinc-100 text-zinc-500";
}

function PointsPill({ value, dim }) {
  if (dim) {
    return (
      <span className="inline-flex items-center justify-center min-w-[2.5rem] h-7 px-2 rounded-sm text-zinc-300 text-sm tabular-nums">
        —
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[2.5rem] h-7 px-2 rounded-sm font-bold text-sm tabular-nums ${getPointsPillClass(
        value
      )}`}
    >
      {value}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sandbox controls — always visible so anonymous visitors from the marketing
// page can experience the full "predict → whistle → see points" flow.
// ---------------------------------------------------------------------------
function SandboxToolbar({ onReset, resetBusy, actualSet }) {
  return (
    <div
      data-testid="demo-admin-toolbar"
      className="sticky top-0 z-40 bg-zinc-950 text-white border-b border-zinc-800"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-300">
          <Sparkles className="h-3.5 w-3.5 text-amber-400" />
          Sandbox controls
          {actualSet ? (
            <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-600/20 text-emerald-300 tracking-wider">
              <CheckCircle2 className="h-3 w-3" /> whistle blown
            </span>
          ) : null}
        </div>
        <Button
          type="button"
          onClick={onReset}
          disabled={resetBusy}
          data-testid="demo-reset"
          variant="outline"
          className="h-9 px-4 rounded-sm border-zinc-700 bg-transparent text-white hover:bg-zinc-800 font-heading font-bold text-xs uppercase tracking-wider"
        >
          {resetBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset demo
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function BusinessDemoPage() {
  const navigate = useNavigate();
  const sessionId = useMemo(() => ensureSessionId(), []);
  const [demoLocked, setDemoLocked] = useState(() => isDemoLocked());
  const [previewId, setPreviewId] = useState(null);
  const [searchParams] = useSearchParams();
  const brandSlug = searchParams.get("slug");
  const [brand, setBrand] = useState(null);

  // Fetch pub brand when ?slug=<pub> is present. Public endpoint —
  // no auth required, safe for the pitch sandbox.
  useEffect(() => {
    if (!brandSlug) {
      setBrand(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/pubs/by-slug/${brandSlug}`);
        if (!cancelled) setBrand(data);
      } catch {
        if (!cancelled) setBrand(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandSlug]);

  const brandPrimary = brand?.primary_color || "#0F766E";
  const brandName = brand?.name || "Dugsy's";
  const brandLogo = brand?.logo_data_url || brand?.logo_url || null;

  // Re-check the lock on mount in case the guard set it after arrival.
  useEffect(() => {
    setDemoLocked(isDemoLocked());
  }, []);

  const handleExitDemo = useCallback(() => {
    clearDemoLock();
    setDemoLocked(false);
    navigate("/football-challenge", { replace: true });
  }, [navigate]);

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [whistleBusy, setWhistleBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const [displayName, setDisplayName] = useState(
    () => localStorage.getItem(NAME_KEY) || ""
  );
  const [values, setValues] = useState({
    goals: "",
    shots_on_target: "",
    corners: "",
    offsides: "",
    throw_ins: "",
    fouls: "",
  });
  const [myPrediction, setMyPrediction] = useState(null);

  const inputRefs = useRef([]);
  const submitBtnRef = useRef(null);

  // ---------------------------------------------------------------------
  // Loaders
  // ---------------------------------------------------------------------
  const reload = useCallback(async () => {
    try {
      const [{ data: s }, { data: me }] = await Promise.all([
        api.get("/business-demo/state"),
        api.get("/business-demo/my-prediction", { params: { session_id: sessionId } }),
      ]);
      setState(s);
      setMyPrediction(me.prediction);
      if (me.prediction?.stats) {
        setValues({
          goals: String(me.prediction.stats.goals ?? ""),
          shots_on_target: String(me.prediction.stats.shots_on_target ?? ""),
          corners: String(me.prediction.stats.corners ?? ""),
          offsides: String(me.prediction.stats.offsides ?? ""),
          throw_ins: String(me.prediction.stats.throw_ins ?? ""),
          fouls: String(me.prediction.stats.fouls ?? ""),
        });
      }
      if (me.prediction?.display_name && !displayName) {
        setDisplayName(me.prediction.display_name);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId, displayName]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ---------------------------------------------------------------------
  // Input helpers — same UX as the real card (digits only, auto-advance).
  // ---------------------------------------------------------------------
  const sanitize = (raw) => String(raw ?? "").replace(/\D/g, "").slice(0, 2);

  const focusIndex = (idx) => {
    const next = inputRefs.current[idx];
    if (next && typeof next.focus === "function") {
      next.focus();
      if (typeof next.select === "function") next.select();
    } else if (idx >= PREDICTION_FIELDS.length && submitBtnRef.current) {
      submitBtnRef.current.focus();
    }
  };

  const handleFieldChange = (idx, key, raw) => {
    const v = sanitize(raw);
    setValues((s) => ({ ...s, [key]: v }));
    if (v.length === 2) focusIndex(idx + 1);
  };

  const handleFieldKeyDown = (idx, key, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (values[key] !== "" && values[key] != null) {
        focusIndex(idx + 1);
      }
    }
  };

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!displayName.trim()) {
      toast.error("Please add a display name first");
      return;
    }
    const payload = {};
    for (const f of PREDICTION_FIELDS) {
      const n = parseInt(values[f.key], 10);
      if (Number.isNaN(n) || n < 0) {
        toast.error(`Enter a valid number for ${f.label}`);
        return;
      }
      payload[f.key] = n;
    }
    setSaving(true);
    localStorage.setItem(NAME_KEY, displayName.trim());
    try {
      const { data } = await api.post("/business-demo/predict", {
        session_id: sessionId,
        display_name: displayName.trim(),
        stats: payload,
      });
      setMyPrediction(data.prediction);
      toast.success(
        data.prediction.points_total !== null
          ? `Prediction submitted — you scored ${data.prediction.points_total}!`
          : "Prediction locked in"
      );
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const simulateWhistle = async () => {
    setWhistleBusy(true);
    try {
      await api.post("/business-demo/simulate-final-whistle");
      toast.success("Full-time! Points calculated.");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWhistleBusy(false);
    }
  };

  const resetDemo = async () => {
    setResetBusy(true);
    try {
      await api.post("/business-demo/reset");
      toast.success("Demo reset — bots re-seeded, ready for the next pitch.");
      setValues({
        goals: "",
        shots_on_target: "",
        corners: "",
        offsides: "",
        throw_ins: "",
        fouls: "",
      });
      setMyPrediction(null);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetBusy(false);
    }
  };

  // ---------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------
  const actual = state?.actual_result || null;
  const actualSet = !!actual;
  const locked = !!myPrediction; // once submitted, mirror real card behaviour
  const formDisabled = locked || actualSet;

  const totalPoints =
    myPrediction?.points_total ??
    PREDICTION_FIELDS.reduce(
      (s, f) => s + pointsFor(values[f.key], actual?.[f.key], f.key),
      0
    );

  // Card status badge (same rules as real MatchCard)
  const statusBadge = actualSet
    ? { label: "FINISHED", className: "bg-zinc-950 text-white hover:bg-zinc-950" }
    : locked
    ? { label: "LOCKED", className: "bg-zinc-900 text-white hover:bg-zinc-900" }
    : { label: "OPEN", className: "bg-[#0038FF] text-white hover:bg-[#0038FF]" };

  return (
    <div className="min-h-screen bg-white" data-testid="business-demo-page">
      {demoLocked ? (
        <div
          data-testid="demo-lock-banner"
          className="sticky top-0 z-50 text-white border-b border-black/10"
          style={{ backgroundColor: brandPrimary }}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em]">
              <Lock className="h-3.5 w-3.5" />
              Demo mode · you&apos;re inside the fan sandbox
            </div>
            <Button
              type="button"
              onClick={handleExitDemo}
              data-testid="demo-exit-button"
              className="h-8 px-3 rounded-sm bg-white hover:bg-white/90 font-heading font-bold text-xs uppercase tracking-wider"
              style={{ color: brandPrimary }}
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Exit demo
            </Button>
          </div>
        </div>
      ) : null}
      <SandboxToolbar
        onReset={resetDemo}
        resetBusy={resetBusy}
        actualSet={actualSet}
      />

      {/* Page header --------------------------------------------------- */}
      <header className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 pb-4">
        <div className="flex items-center gap-3">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              data-testid="demo-brand-logo"
              className="h-10 w-10 rounded-sm object-contain bg-white border border-zinc-200"
            />
          ) : (
            <div
              className="h-10 w-10 rounded-sm inline-flex items-center justify-center"
              style={{ backgroundColor: brand ? brandPrimary : "#0a0a0a" }}
            >
              <Beer className="h-5 w-5 text-white" />
            </div>
          )}
          <div>
            <p
              className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500"
              data-testid="demo-brand-label"
            >
              {brandName} · Pitch sandbox
            </p>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              Live demo — the exact match-day experience.
            </h1>
          </div>
        </div>
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-900">
          <Sparkles className="h-3.5 w-3.5" />
          <p className="text-[11px] font-semibold">
            Sandbox mode. Nothing you do here touches real match-day standings
            or triggers emails.
          </p>
        </div>
      </header>

      {loading || !state ? (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center text-zinc-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          <p className="mt-3 text-xs uppercase tracking-widest">Loading demo…</p>
        </div>
      ) : (
        <main className="max-w-4xl mx-auto px-4 sm:px-6 pb-16 space-y-8">
          {/* ============================================================
              MATCH CARD — mirrors MatchCard from pages/MatchesPage.jsx
             ============================================================ */}
          <article
            data-testid={`match-card-${state.match.id}`}
            className="border border-zinc-200 rounded-sm bg-white"
          >
            <header className="px-6 py-5 border-b border-zinc-200 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                    {state.match.kickoff_local} · {state.match.match_date}
                  </span>
                  <Badge
                    className={`rounded-sm text-[10px] tracking-widest ${statusBadge.className}`}
                  >
                    {statusBadge.label}
                  </Badge>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                    {state.match.competition}
                  </span>
                </div>
                <h2 className="mt-1 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">
                  {state.match.home_team}{" "}
                  <span className="text-zinc-300">vs</span> {state.match.away_team}
                </h2>
              </div>
              <div className="text-right text-[11px] text-zinc-500 leading-tight hidden sm:block">
                <p>{state.match.venue}</p>
              </div>
            </header>

            <form onSubmit={submit} className="p-6">
              {/* Display name input (demo-only — real matches take name from auth) */}
              {!locked && (
                <div className="mb-4">
                  <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                    Your name
                  </Label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value.slice(0, 40))}
                    placeholder="e.g. Carl at The Founders Arms"
                    data-testid="demo-name-input"
                    disabled={saving}
                    className="mt-1.5 h-11 rounded-sm border-zinc-300 max-w-md"
                  />
                </div>
              )}

              {/* Instruction copy — same phrasing as real card */}
              {!locked && !actualSet && (
                <p className="text-sm text-zinc-500 mb-4">
                  Enter combined match totals (both teams together). Fields
                  auto-advance as you type — press{" "}
                  <kbd className="px-1.5 py-0.5 rounded-sm border border-zinc-300 bg-zinc-50 text-[11px] font-semibold text-zinc-700">
                    Enter
                  </kbd>{" "}
                  on a single-digit value to jump to the next. One submission
                  per match — no edits after.
                </p>
              )}

              {/* Three-row grid: Prediction / Actual / Points */}
              <div className="overflow-x-auto -mx-6 sm:mx-0 px-6 sm:px-0">
                <div className="min-w-[720px]">
                  {/* Column headers */}
                  <div
                    className="grid gap-3 items-end pb-3 border-b border-zinc-200"
                    style={{
                      gridTemplateColumns: `120px repeat(${PREDICTION_FIELDS.length}, minmax(0, 1fr))`,
                    }}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                      &nbsp;
                    </div>
                    {PREDICTION_FIELDS.map((f) => (
                      <div
                        key={f.key}
                        className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 text-center"
                      >
                        {f.label}
                      </div>
                    ))}
                  </div>

                  {/* Row 1: Predictions */}
                  <div
                    className="grid gap-3 items-center py-3 border-b border-zinc-100"
                    style={{
                      gridTemplateColumns: `120px repeat(${PREDICTION_FIELDS.length}, minmax(0, 1fr))`,
                    }}
                  >
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-950">
                      Prediction
                    </Label>
                    {PREDICTION_FIELDS.map((f, idx) => {
                      if (locked) {
                        return (
                          <div
                            key={f.key}
                            data-testid={`predict-${f.key}-demo`}
                            className="h-11 flex items-center justify-center rounded-sm bg-zinc-50 border border-zinc-200 text-base font-semibold tabular-nums text-zinc-950 relative"
                            aria-label={`${f.label} (locked)`}
                          >
                            {values[f.key]}
                            <Lock
                              className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-400"
                              aria-hidden="true"
                            />
                          </div>
                        );
                      }
                      return (
                        <Input
                          key={f.key}
                          id={`demo-${f.key}`}
                          ref={(el) => {
                            inputRefs.current[idx] = el;
                          }}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={2}
                          autoComplete="off"
                          disabled={formDisabled}
                          required={!formDisabled}
                          value={values[f.key]}
                          onChange={(e) => handleFieldChange(idx, f.key, e.target.value)}
                          onKeyDown={(e) => handleFieldKeyDown(idx, f.key, e)}
                          onFocus={(e) => e.target.select?.()}
                          data-testid={`predict-${f.key}-demo`}
                          aria-label={`Prediction ${f.label}`}
                          className="h-11 rounded-sm border-zinc-300 text-base font-semibold text-center focus-visible:ring-[#0038FF] disabled:bg-zinc-50 disabled:text-zinc-950 disabled:opacity-100"
                        />
                      );
                    })}
                  </div>

                  {/* Progressive disclosure: Actual + Points */}
                  <div
                    data-testid="results-disclosure-demo"
                    className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
                      actualSet ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                    aria-hidden={!actualSet}
                  >
                    <div className="overflow-hidden">
                      {/* Row 2: Actual */}
                      <div
                        className="grid gap-3 items-center py-3 border-b border-zinc-100"
                        style={{
                          gridTemplateColumns: `120px repeat(${PREDICTION_FIELDS.length}, minmax(0, 1fr))`,
                        }}
                        data-testid="actual-row-demo"
                      >
                        <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                          Actual
                        </Label>
                        {PREDICTION_FIELDS.map((f) => (
                          <div
                            key={f.key}
                            data-testid={`actual-${f.key}-demo`}
                            className="h-11 flex items-center justify-center text-base font-semibold tabular-nums rounded-sm border border-dashed border-zinc-200 bg-white"
                          >
                            {actual ? (
                              actual[f.key]
                            ) : (
                              <span className="text-zinc-300">—</span>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Row 3: Points */}
                      <div
                        className="grid gap-3 items-center py-3"
                        style={{
                          gridTemplateColumns: `120px repeat(${PREDICTION_FIELDS.length}, minmax(0, 1fr))`,
                        }}
                        data-testid="points-row-demo"
                      >
                        <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                          Points
                        </Label>
                        {PREDICTION_FIELDS.map((f) => {
                          const p = values[f.key];
                          const a = actual?.[f.key];
                          const ready = myPrediction != null && actual != null;
                          const pts = ready ? pointsFor(p, a, f.key) : 0;
                          return (
                            <div
                              key={f.key}
                              className="flex items-center justify-center"
                              data-testid={`points-${f.key}-demo`}
                            >
                              <PointsPill value={pts} dim={!ready} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer area */}
              <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                <span
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-zinc-500"
                  data-testid="demo-footer-text"
                >
                  {(locked || actualSet) && <Lock className="h-3.5 w-3.5" />}
                  {actualSet
                    ? `Final · Total ${totalPoints} pts`
                    : locked
                    ? "Submitted · awaiting result"
                    : "Not submitted"}
                </span>

                {!locked && !actualSet && (
                  <Button
                    type="submit"
                    ref={submitBtnRef}
                    disabled={saving}
                    data-testid="demo-submit"
                    className="h-11 px-6 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold tracking-tight active:scale-[0.98] transition-colors"
                  >
                    {saving ? "Submitting…" : "Submit prediction"}
                  </Button>
                )}
                {(locked || actualSet) && (
                  <CheckCircle2 className="h-5 w-5 text-[#0038FF]" aria-hidden="true" />
                )}
              </div>
            </form>
          </article>

          {/* Simulate final whistle — sits directly under the Submit button,
              same size + shape. Amber to visually flag "next step". */}
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={simulateWhistle}
              disabled={whistleBusy || actualSet}
              data-testid="demo-simulate-whistle"
              className="h-11 px-6 rounded-sm bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold tracking-tight active:scale-[0.98] transition-colors disabled:bg-zinc-200 disabled:text-zinc-400"
            >
              {whistleBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <AlarmClock className="h-4 w-4 mr-2" />
                  {actualSet ? "Final whistle blown" : "Simulate final whistle"}
                </>
              )}
            </Button>
          </div>

          {/* ============================================================
              DAILY LEAGUE — below the match card
             ============================================================ */}
          <section
            className="border border-zinc-200 rounded-sm bg-white"
            data-testid="demo-leaderboard"
          >
            <div className="px-6 py-5 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-500" />
                <h3 className="font-heading text-xl font-bold text-zinc-950">
                  Demo daily league
                </h3>
              </div>
              <p className="text-[11px] text-zinc-500 uppercase tracking-widest">
                {actualSet
                  ? `${state.leaderboard.length} players · updated live`
                  : "Waiting for the whistle…"}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100">
                    <th className="text-left px-6 py-3 w-14">#</th>
                    <th className="text-left px-6 py-3">Player</th>
                    <th className="text-right px-6 py-3 w-28">Correct</th>
                    <th className="text-right px-6 py-3 w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {state.leaderboard.map((r) => {
                    const mine = r.session_id === sessionId;
                    return (
                      <tr
                        key={r.session_id}
                        data-testid={`demo-leaderboard-row-${r.session_id}`}
                        className={`border-b border-zinc-100 last:border-b-0 ${
                          mine ? "bg-[#0038FF]/5" : ""
                        }`}
                      >
                        <td className="px-6 py-3 font-heading font-bold text-zinc-950 tabular-nums">
                          {r.rank ?? "—"}
                        </td>
                        <td className="px-6 py-3 text-sm text-zinc-800">
                          {r.display_name}
                          {mine ? (
                            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#0038FF] text-white text-[10px] font-bold uppercase tracking-widest">
                              You
                            </span>
                          ) : r.is_bot ? (
                            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-500 text-[10px] font-bold uppercase tracking-widest">
                              Demo
                            </span>
                          ) : null}
                        </td>
                        <td className="px-6 py-3 text-right text-sm text-zinc-700 tabular-nums">
                          {r.correct_count ?? "—"}
                        </td>
                        <td className="px-6 py-3 text-right font-heading font-bold text-zinc-950 tabular-nums">
                          {r.points_total ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* ============================================================
              PLAYER JOURNEY — emails players receive, in order
             ============================================================ */}
          <section data-testid="demo-player-journey">
            <header className="mb-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                What players hear from you
              </p>
              <h3 className="mt-1 font-heading text-xl md:text-2xl font-bold text-zinc-950">
                Every touchpoint, in the order it lands.
              </h3>
              <p className="mt-2 text-sm text-zinc-500 max-w-2xl">
                From sign-up to prize claim — fully automated, fully branded.
                Zero admin for the pub.
              </p>
            </header>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                {
                  step: "01",
                  icon: Mail,
                  title: "Welcome email",
                  body:
                    "Right after sign-up. Confirms account, links to their first prediction card, sets expectations.",
                  testid: "journey-01-welcome",
                },
                {
                  step: "02",
                  icon: Bell,
                  title: "Kickoff reminder",
                  body:
                    "~2 hours before every match. Nudges players to lock in their prediction before the whistle.",
                  testid: "journey-02-kickoff",
                },
                {
                  step: "03",
                  icon: ClipboardList,
                  title: "Daily league update",
                  body:
                    "After each match day. Their score, their rank, movement up/down, and the top 10 in their pub.",
                  testid: "journey-03-daily",
                },
                {
                  step: "04",
                  icon: Users,
                  title: "Mini-league invites",
                  body:
                    "Invite mates, workmates, family — private leagues within the pub league for extra bragging rights.",
                  testid: "journey-04-mini-leagues",
                },
                {
                  step: "05",
                  icon: MessageCircle,
                  title: "Community WhatsApp",
                  body:
                    "Optional invite to the pub's WhatsApp banter group — banter, predictions, community, all in one place.",
                  testid: "journey-05-whatsapp",
                },
                {
                  step: "06",
                  icon: ClipboardList,
                  title: "Final standings email",
                  body:
                    "When the season closes, every player receives the final league table — where they finished, top 10 in the pub, and total points.",
                  testid: "journey-06-final-standings",
                },
                {
                  step: "07",
                  icon: Award,
                  title: "Season champion",
                  body:
                    "The winner gets a personalised congratulations email with prize details right after the final standings land.",
                  testid: "journey-07-champion",
                },
                {
                  step: "08",
                  icon: Ticket,
                  title: "Prize & voucher claim",
                  body:
                    "Winners receive their voucher by email with a scan-code to redeem at your pub. Expiry reminders too.",
                  testid: "journey-08-voucher",
                },
              ].map(({ step, icon: Icon, title, body, testid }) => (
                <button
                  type="button"
                  key={testid}
                  data-testid={testid}
                  onClick={() => setPreviewId(testid)}
                  style={{ backgroundColor: brandPrimary }}
                  className="rounded-sm p-5 text-white text-left transition-transform hover:-translate-y-0.5 hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F18D49] focus-visible:ring-offset-2"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-sm bg-white/10 border border-white/20">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/70">
                        {step} · Email
                      </p>
                      <h4 className="font-heading text-base font-bold text-white leading-tight">
                        {title}
                      </h4>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-white/90">
                    {body}
                  </p>
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.22em] text-white/70">
                    Tap to preview →
                  </p>
                </button>
              ))}
            </div>
          </section>
        </main>
      )}
      <EmailPreviewDialog
        openId={previewId}
        onClose={() => setPreviewId(null)}
        brand={brand ? { name: brandName, primary: brandPrimary, logo: brandLogo, slug: brandSlug } : null}
      />
    </div>
  );
}
